import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  Menu,
  MenuItem,
  ListItemIcon,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
  SvgIcon,
  ToggleButton,
  ToggleButtonGro,
  AppBar,
  Toolbarup,
  Typography,
  Drawer,
  ToggleButtonGroup, IconButton, useMediaQuery
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { useTheme } from '@mui/material/styles';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import ShareIcon from '@mui/icons-material/Share';
import AddCircleIcon from '@mui/icons-material/AddCircle';

import { Link } from 'react-router-dom';
import ChatBotDrawer from './ChatBotDrawer';
import JopApplicationDrawer from './JopApplicationDrawer';
import UpdateReleventSkillsDrawer from './UpdateReleventSkillsDrawer';
import AutoSavingJobAppDialog from './AutoSavingJobAppDialog';
import UnsaveCard from './UnsaveCard';
import ExternalJob from './ExternalJob';
import ExternalJobStatus from './ExternalJobStatus';
import { useForm, Controller } from 'react-hook-form';
import { saveJobApplication, getApplicationApplyDetails } from 'api/application';
import { useSnackbar } from 'components/snackbar';

import { getTimeDifferenceString, changeNameFormat, formatNumber, toTitleCase } from 'utils/Utils';

import { SuitableChip, ModerateChip, NotSuitableChip, CheckIcon, CrossIcon, WarningIcon } from '../icon/icon';
import { useAuth } from 'hooks/useAuthContext';
import ShareJob from './ShareJob';
import dayjs from 'dayjs';
import { useBackNavigationDialog } from 'hooks/useBackNavigationDialog';

const JobFullDetails = ({ data, onApplied, savedJob, handleChangeSaveOrUnsaveButton,
  handleSaveOrUnsaveJob, unsaveJob, downloadResume, showExternalJobStatus,
  onJobPreferencesChange, updateTJScore, handleCloseDrawer
}) => {
  const { jobApply } = useAuth();
  const { openDialog, closeDialog, dialogLocation } = useBackNavigationDialog();
  const dialogIds = dialogLocation.state?.dialogIds || [];

  const [alignment, setAlignment] = useState('job-overview');
  const [candidateDetails, setCandidateDetails] = useState({});
  const jobOverviewRef = useRef(null);
  const jobFullOverviewRef = useRef(null);
  const jobDescRef = useRef(null);
  const [requirementMatchItem] = useState([
    'Offered Compensation',
    'Job Location',
    'Work Format',
    'Job Type',
    'Working Hours',
    'Notice Period',
    'Time Commitment'
  ]);
  const [matchingSkills, setMatchingSkills] = useState([]);
  const [nonMatchingSkills, setNonMatchingSkills] = useState([]);
  const [dataSubmitted, setDataSubmitted] = useState(true);
  const { control, handleSubmit } = useForm();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [coreMatchSkills, setCoreMatchSkills] = useState([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [showMore, setShowMore] = useState(false);
  const isManualScroll = useRef(false);
  const [resumeActionAnchorEl, setResumeActionAnchorEl] = useState(null);
  const openActionMenu = Boolean(resumeActionAnchorEl);
  const [openTooltip, setOpenTooltip] = useState('');

  // const [open, setOpen] = useState(false);
  // const [openSkillsDrawer, setOpenSkillsDrawer] = useState(false);
  // const [openChatBotDrawer, setOpenChatBotDrawer] = useState(localStorage.getItem('openChatBot') === 'true' && (jobApply?.job?._id === data?._id && !(jobApply?.job?.external_link)) ? jobApply?.openChatBot : false);

  const openJobPreference = dialogIds.includes("jobPreference");
  const openSkillsDrawer = dialogIds.includes("skillsDrawer");
  const shareJobDialogOpen = dialogIds.includes("shareJobDialog");
  const autoUnsaveDialogOpen = dialogIds.includes("UnsaveDialog");
  const openExternalJobStatus = dialogIds.includes("openExternalJobStatus");
  const openChatBotDrawer = dialogIds.includes("chatBotDrawer");
  // const autoSaveDialogOpen = dialogIds.includes("autoSaveDialog");
  const openExternalJob = dialogIds.includes("externalJob");


  const handleOpenJobPreferenceDrawer = () => openDialog('jobPreference');
  const handleCloseJobPreferenceDrawer = () => closeDialog('jobPreference');
  const handleOpenSkillsDrawer = () => openDialog('skillsDrawer');
  const handleCloseSkillsDrawer = () => closeDialog('skillsDrawer');
  const handleOpenShareJobDialog = () => openDialog('shareJobDialog');
  const handleCloseShareJobDialog = () => closeDialog('shareJobDialog');
  const handleOpenChatBotDrawer = () => openDialog('chatBotDrawer');
  const handleCloseChatBotDrawer = () => closeDialog('chatBotDrawer');
  // const handleOpenAutoSaveDialog = () => openDialog('autoSaveDialog');
  // const handleCloseAutoSaveDialog = () => closeDialog('autoSaveDialog');
  const handleOpenAutoUnsaveDialog = () => openDialog('UnsaveDialog');
  const handleCloseAutoUnsaveDialog = () => {
    closeDialog('UnsaveDialog');
    handleChangeSaveOrUnsaveButton('unsaveJob');
  }
  const handleOpenExternalJob = () => openDialog('externalJob');
  const handleCloseExternalJob = () => closeDialog('externalJob');
  const handleOpenExternalJobStatus = () => openDialog('openExternalJobStatus');
  const handleCloseExternalJobStatus = () => closeDialog('openExternalJobStatus');

  /**
   * Asynchronously fetches application details and updates relevant states.
   *
   * @param {boolean} [recommend=false] - Flag to include recommendations in the fetch request.
   *
   * Fetches application details using the job ID from the data object. If successful, updates
   * candidate details and matching skills based on the response. Also updates the TJ score if
   * necessary. Handles the status of saved and unsaved jobs, and manages the display of external
   * job status. Logs errors to the console if fetching fails.
   */

  const fetchData = async (recommend = false) => {
    try {
      const response = await getApplicationApplyDetails({ id: data?._id, recommend: recommend });
      if (response?.success && response?.data != null) {
        setCandidateDetails(response?.data);

        // console.log('Rating:', response?.data?.rating)
        // console.log('TjScore:', response?.data?.aggregateScore);

        const TjScore = response?.data?.aggregateScore;
        if (recommend || (data.tj_score !== TjScore)) {
          updateTJScore(TjScore);
          data.tj_score = TjScore;
        }
        setMatchingSkills(
          handleMatchingSkills(
            response?.data?.workHistory || [],
            response?.data?.certificate || [],
            response?.data?.educations || [],
            data?.key_skills || [],
            data?.core_skills || []
          )
        );
        if (showExternalJobStatus === 'showExternalStatus') {
          handleExternalJobStatus();
        }
      } else {
        console.error('Failed to fetch job info:', response?.data?.message);
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Compares and identifies matching skills between a candidate's profile and job requirements.
   *
   * This function processes the candidate's work history, certificates, and education to extract skills,
   * normalizes them to lowercase, and removes duplicates. It then compares these skills with the job's
   * required and core skills to determine matches. The matched skills are returned, while non-matching
   * skills are categorized based on their core status and stored in state variables.
   *
   * @param {object[]} workHistory - An array of objects representing the candidate's work history.
   * @param {object[]} certificate - An array of objects representing the candidate's certificates.
   * @param {object[]} education - An array of objects representing the candidate's education.
   * @param {string[]} jobSkills - An array of strings representing the skills required for the job.
   * @param {string[]} coreSkills - An array of strings representing the core skills required for the job.
   * @returns {string[]} - An array of strings representing the skills that match between the candidate and the job.
   */

  const handleMatchingSkills = (workHistory, certificate, education, jobSkills, coreSkills) => {
    if (!workHistory || !Array.isArray(workHistory)) {
      console.error('Invalid work history provided.');
      return [];
    }
    if (!jobSkills || !Array.isArray(jobSkills)) {
      console.error('Invalid job skills provided.');
      return [];
    }
    let candidateSkills = [];
    workHistory?.forEach((history) => {
      if (history?.skills && Array.isArray(history?.skills)) {
        candidateSkills.push(...history.skills.map((skill) => skill.toLowerCase()));
      } else {
        console.warn('Invalid or missing skills property in work history entry:', history);
      }
    });
    certificate?.forEach((history) => {
      if (history?.skills && Array.isArray(history?.skills)) {
        candidateSkills.push(...history.skills.map((skill) => skill.toLowerCase()));
      } else {
        console.warn('Invalid or missing skills property in work history entry:', history);
      }
    });
    education?.forEach((history) => {
      if (history?.skills && Array.isArray(history?.skills)) {
        candidateSkills.push(...history.skills.map((skill) => skill.toLowerCase()));
      } else {
        console.warn('Invalid or missing skills property in work history entry:', history);
      }
    });
    const uniqueCandidateSkills = [...new Set(candidateSkills)];
    const matchingSkills = [];
    const nonMatchingSkills = [];

    jobSkills?.forEach((skill) => {
      const lowerCaseSkill = skill.toLowerCase();
      if (uniqueCandidateSkills?.includes(lowerCaseSkill)) {
        matchingSkills.push(skill);
      } else {
        nonMatchingSkills.push({ skill, is_core: coreSkills?.includes(skill) });
      }
    });
    let coreMatchSkill = [];

    coreSkills?.forEach((skill) => {
      const lowerCaseSkill = skill.toLowerCase();
      if (uniqueCandidateSkills?.includes(lowerCaseSkill)) {
        coreMatchSkill.push(skill);
      }
    });
    setCoreMatchSkills(coreMatchSkill);
    setNonMatchingSkills(nonMatchingSkills);

    return matchingSkills;
  };

  useEffect(() => {
    setAlignment('job-overview');
    setDataSubmitted(true);
    setLoading(true);
    fetchData();
  }, [data]);

  useEffect(() => {
    if (jobApply?.job?._id === data?._id && jobApply?.openChatBot) {
      if (jobApply?.job?.external_link) handleOpenExternalJob();
      else handleOpenChatBotDrawer();
    } else {
      handleCloseChatBotDrawer();
    }
  }, []);


  useEffect(() => {
    if (data?.application_saved) {
      if (data?._id === unsaveJob) {
        handleOpenAutoUnsaveDialog();
      }
    } else {
      if (data?._id === savedJob) {
        // handleSaveForLater();
        setDataSubmitted(false);
      } else {
        setDataSubmitted(true);
      }
    }
  }, [savedJob, unsaveJob]);

  // useEffect(() => {
  //   if (isMobile) {
  //     if (openChatBotDrawer) {
  //       localStorage.setItem('job', JSON.stringify(data));
  //     } else localStorage.removeItem('job');
  //   }
  // }, [openChatBotDrawer]);

  const handleScroll = useCallback(() => {
    if (isManualScroll.current || !jobFullOverviewRef.current) return;

    const container = jobFullOverviewRef.current;
    const scrollPosition = container.scrollTop;

    const sections = [
      { id: "job-overview", ref: jobOverviewRef },
      { id: "job-desc", ref: jobDescRef },
    ];

    for (const section of sections) {
      if (!section.ref.current) continue;

      const offsetTop = section.ref.current.offsetTop;
      const height = section.ref.current.clientHeight;

      if (
        scrollPosition >= offsetTop - 100 &&
        scrollPosition < offsetTop + height - 100
      ) {
        if (alignment !== section.id) {
          setAlignment(section.id);
        }
        break;
      }
    }
  }, [alignment]);

  useEffect(() => {

    const container = jobFullOverviewRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll);

    handleScroll();

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll, loading]);

  /**
   * A higher-order function that returns a function to toggle the state of the drawer.
   * @param {boolean} newOpen - The desired state of the drawer.
   * @returns {function} - A function that, when called, sets the drawer to the specified state.
   */

  // const toggleDrawer = (newOpen) => () => {
  //   setOpen(newOpen);
  // };

  /**
   * A higher-order function that takes a boolean and returns a function that
   * toggles the state of the skills drawer to that boolean value.
   * @param {boolean} newOpenSkills - The desired state of the skills drawer.
   * @returns {function} - A function that takes no arguments and toggles the drawer
   */
  // const toggleSkillsDrawer = (newOpenSkills) => () => {
  //   setOpenSkillsDrawer(newOpenSkills);
  // };

  // const [shareJobDialogOpen, setShareJobDialogOpen] = useState(false);
  /**
   * Opens the share job dialog and closes the resume action menu.
   */
  // const handleOpenShareJobDialog = () => {
  //   setShareJobDialogOpen(true);
  //   setResumeActionAnchorEl(null);
  // };
  /**
   * Closes the share job dialog.
   */
  // const handleCloseShareJobDialog = () => {
  //   setShareJobDialogOpen(false);
  // };

  /**
   * A higher-order function that takes a boolean and returns a function that
   * toggles the state of the chat bot drawer to that boolean value.
   * @param {boolean} newOpenSkills - The desired state of the chat bot drawer.
   * @returns {function} - A function that takes no arguments and toggles the drawer
   */
  const toggleChatBotDrawer = (newOpenSkills) => () => {
    // setOpenChatBotDrawer(newOpenSkills);
    newOpenSkills ? handleOpenChatBotDrawer() : handleCloseChatBotDrawer();
  };

  // auto saving job app dialog
  const [autoSaveDialogOpen, setAutoSetDialogOpen] = useState(false);

  /**
   * Closes the auto saving job app dialog.
   */
  const handleCloseAutoSaveDialog = () => {
    setAutoSetDialogOpen(false);
  };

  /**
   * Handles changes to the ToggleButtonGroup.
   * When the toggle value changes, it updates the alignment state variable and
   * scrolls to the corresponding element in the job details section.
   * @param {React.ChangeEvent<HTMLInputElement>} event - The event from the ToggleButtonGroup.
   * @param {string} newAlignment - The new alignment value from the ToggleButtonGroup.
   */
  const handleToggleChange = (event, newAlignment) => {
    isManualScroll.current = true;
    if (newAlignment !== null) {
      setAlignment(newAlignment);
      let targetRef;

      switch (newAlignment) {
        case 'job-overview':
          targetRef = jobOverviewRef;
          break;
        case 'job-desc':
          targetRef = jobDescRef;
          break;
        default:
          break;
      }

      jobFullOverviewRef.current.scrollTo({
        top: targetRef.current.offsetTop,
        block: 'start',
        behavior: 'smooth',
      });

      setTimeout(() => {
        isManualScroll.current = false;
      }, 500);
    }
  };

  /**
   * Renders a match icon based on job and candidate preferences.
   *
   * This function determines the match status between a job and a candidate,
   * and returns a React element containing an icon that represents the match status
   * along with the title of the preference. The icon varies based on the match:
   * - <CheckIcon /> for a strong match,
   * - <WarningIcon /> for a moderate match,
   * - <CrossIcon /> for no match.
   *
   * If the title is 'Offered Compensation' and the job is not provided, it displays
   * a default icon with a rupee symbol.
   *
   * @param {any} job - The job object.
   * @param {any} candidate - The candidate preference.
   * @param {string} title - The title of the preference.
   * @returns {React.ReactElement} A React element containing the match icon and the title of the preference.
   */

  const matchIcon = (job, candidate, title) => {
    let match = 1;

    if (title === 'Offered Compensation') {
      if (!job) return (
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Box className="off-comp-rupees" sx={{ fontFamily: 'Inter, sans-serif!important' }}>
              ₹
            </Box>
            <Typography className="pref-match-title">{title || 'NA'}</Typography>
          </Stack>
        </Box>
      )
      match = candidate === 4 ? 3 : candidate;
    } else {
      match = candidate === 2 ? 3 : 1;
    }

    const IconComponent = match === 3 ? CheckIcon : match === 2 ? WarningIcon : CrossIcon;

    return (
      <Box>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <IconComponent />
          <Typography className="pref-match-title">{title || 'NA'}</Typography>
        </Stack>
      </Box>
    );
  };

  /**
   * Handles the logic for saving a job application for later.
   * If the job is not already saved, it calls the handleSaveOrUnsaveJob function to save the job.
   * If candidate details are available, it calls the saveJobApplication API to save the job application.
   * If the API call is successful, it updates the application status and displays a success message.
   * If the API call fails, it displays an error message.
   * If no candidate details are available, it displays an error message.
   * Finally, it calls the handleChangeSaveOrUnsaveButton function to update the save or unsave button.
   * @returns {Promise<void>}
   */
  const handleSaveForLater = async () => {
    try {
      setDataSubmitted(false);
      if (savedJob === '') {
        handleSaveOrUnsaveJob(data._id, 'saveJob');
      }
      if (candidateDetails && Object.keys(candidateDetails).length > 0) {
        const response = await saveJobApplication({ jobId: data?._id, companyId: data?.company_id, type: 'saved' });
        if (response?.success && response?.data != null) {
          enqueueSnackbar('Job successfully saved for later', { variant: 'success' });
          data.application_saved = response?.data?.status === 'saved';
        } else {
        enqueueSnackbar(response?.message, { variant: 'error' });
        }
      } else {
        enqueueSnackbar('Failed to save the application. Please try again.', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error saving for later:', error);
    } finally {
      handleChangeSaveOrUnsaveButton('saveJob');
      setDataSubmitted(true);
    }
  };

  /**
   * Handles the logic for unsaving a job application.
   * If the job is not already unsaved, it calls the handleSaveOrUnsaveJob function to unsave the job.
   * If candidate details are available, it calls the saveJobApplication API to unsave the job application.
   * If the API call is successful, it updates the application status, closes the "Auto Unsave Job" dialog, and displays a success message.
   * If the API call fails, it displays an error message.
   * If no candidate details are available, it displays an error message.
   * Finally, it catches any errors and logs them to the console.
   * @returns {Promise<void>}
   */
  const handleUnsaveJob = async () => {
    try {
      if (unsaveJob === '') {
        handleSaveOrUnsaveJob(data._id, 'unsaveJob');
      }
      if (candidateDetails && Object.keys(candidateDetails).length > 0) {
        const response = await saveJobApplication({ jobId: data?._id, companyId: data?.company_id, type: 'unsaved' });
        if (response?.success) {
          data.application_saved = false;
          handleCloseAutoUnsaveDialog();
        } else {
          enqueueSnackbar('Failed to Unsave the application. Please try again.', { variant: 'error' });
        }
      } else {
        enqueueSnackbar('Failed to Unsave the application. Please try again.', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error unsaving for later:', error);
    }
  };

  /**
   * Checks if the candidate matches the job requirements in a given field.
   * @param {Object} job - The job object.
   * @param {Object} candidate - The candidate object.
   * @param {string} title - The field to check. One of 'Qualification', 'Experience', or 'Skills'.
   * @returns {React.ReactElement} - An icon indicating whether the candidate matches the requirement.
   *   - A <CheckIcon /> if the candidate matches the requirement.
   *   - A <CrossIcon /> if the candidate does not match the requirement.
   *   - A <WarningIcon /> if the candidate partially matches the requirement.
   */
  const requirementsMatch = (job, candidate, title) => {
    let match = 1;
    if (title === 'Qualification') match = candidate === 4 ? 3 : 1;
    if (title === 'Experience' || title === 'Skills') match = candidate === 4 ? 3 : candidate;
    if (title === 'Industry') match = candidate === 4 ? 3 : 2;

    if (match === 3) {
      return (
        <CheckIcon />
      )
    } else if (match === 2) {
      return (
        <WarningIcon />
      )
    } else {
      return (
        <CrossIcon />
      )
    }
  };

  /**
   * Handles the event when a resume is selected from the chatbot dialog.
   * @param {Object} resume - The selected resume object.
   * @param {Object} answers - The answers provided by the user in the chatbot dialog.
   * @returns {void} - nothing
   */
  const handleSelectedResume = (resume, answers) => {
    toggleChatBotDrawer(false);
    onApplied();
  };

  /**
   * Handles the event when a job preference is updated from the chatbot dialog.
   * Refreshes the job details based on the updated preference.
   * @param {Object} preference - The updated job preference object.
   * @returns {Promise<void>} - A promise that resolves when the job details are refreshed.
   */
  const handleUpdatePreference = async (preference) => {
    onJobPreferencesChange();
    await fetchData(true);
  };

  /**
   * Handles the update of skills for a job. Reloads the job details
   * after the update is successful.
   * @param {Object} skills - The updated skills for the job
   * @returns {Promise<void>}
   */
  const handleSkillsUpdate = async (skills) => {
    await fetchData(true);
  };

  const recommendedChip = (score) => score >= 3 ? <SuitableChip /> : score >= 2 ? <ModerateChip /> : <NotSuitableChip />;

  // const [autoUnsaveDialogOpen, setAutoUnsaveDialogOpen] = useState(false);

  /**
   * Opens the "Auto Unsave Job" dialog.
   * This dialog is used to confirm with the user if they want to automatically unsave the job application
   * when they update their resume while applying to a job. It provides options to continue or cancel the process.
   */
  // const handleOpenAutoUnsaveDialog = () => {
  //   setAutoUnsaveDialogOpen(true);
  // };

  /**
   * Closes the "Auto Unsave Job" dialog.
   * Sets the autoUnsaveDialogOpen state to false and updates the save/unsave button state.
   */

  // const handleCloseAutoUnsaveDialog = () => {
  //   setAutoUnsaveDialogOpen(false);
  //   handleChangeSaveOrUnsaveButton('unsaveJob');
  // };

  /**
 * Triggers a data fetch to refresh job details when there are non-matching skills.
 * This function is used to ensure that the job details are up-to-date
 * in response to changes in the skill set that do not match the job requirements.
 */

  const handleNonMatchingSkills = () => {
    fetchData();
  };

  // const [openExternalJob, setOpenExternalJob] = useState((jobApply?.job?._id === data?._id && jobApply?.job?.external_link) ? jobApply?.openChatBot : false);
  /**
   * Handles the event when the user clicks the "Apply" button.
   * If the job has an external link, opens the external job dialog.
   * Otherwise, opens the chatbot dialog.
   * @returns {void} - nothing
   */
  const handleApplyJob = () => {
    if (data?.external_link) {
      // setOpenExternalJob(true);
      handleOpenExternalJob();
    } else {
      // setOpenChatBotDrawer(true);
      handleOpenChatBotDrawer();
    }
  };

  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  useEffect(() => {
    if (openConfirmationDialog && !openExternalJob) {
      const timer = setTimeout(() => {
        handleExternalJobStatus();
        setOpenConfirmationDialog(false);
      }, 500); // or 100ms if needed

      return () => clearTimeout(timer);
    }
  }, [openConfirmationDialog, openExternalJob]);

  /**
   * Closes the external job dialog box.
   */
  const handleCloseExternalJobDialog = (val) => {
    // setOpenExternalJob(false);
    handleCloseExternalJob();
    if (val === 'open External Job Dialog') setOpenConfirmationDialog(true);
  };

  // const [openExternalJobStatus, setOpenExternalJobStatus] = useState(false);
  /**
   * Opens the external job status dialog box.
   * Sets the openExternalJobStatus state to true to display the dialog box.
   * @returns {void} - nothing
   */
  const handleExternalJobStatus = () => {
    // setOpenExternalJobStatus(true);
    handleOpenExternalJobStatus();
  };
  /**
   * Handles the close of the external job status dialog.
   * If the msg is equal to 'applied', reloads the job details.
   * @param {string} msg - The message from the dialog.
   */
  const handleCloseExternalJobStatusDialog = (msg) => {
    // setOpenExternalJobStatus(false);
    handleCloseExternalJobStatus();
    if (msg === "applied") {
      onApplied();
    }
  };
  /**
   * Handles the download of the resume for the job application.
   * It creates a data object with the job, candidate details, matching skills
   * and the tab, and calls the downloadResume function with this object.
   * After the download is complete, it closes the external job dialog.
   */
  const handleDownloadResume = () => {
    const dataForExternalJob = { job: data, candidateDetails: candidateDetails, matchingSkills: matchingSkills, tab: '2' };
    downloadResume(dataForExternalJob);
    handleCloseExternalJobDialog();
  };

  /**
   * Extracts and returns unique industry types from a list of work history items.
   * Iterates over each work item to collect industry sectors and ensures uniqueness.
   * @param {Object[]} work - Array of work history items with organization details.
   * @returns {string[]} - Array of unique industry types.
   */

  const handleIndustryType = (work) => {
    const industryType = [];
    work?.forEach((item) => {
      const sector = item?.organization_details?.industry_sector;
      if (sector) industryType.push(sector);
    });
    const uniqueIndustryType = [...new Set(industryType)];
    return uniqueIndustryType;
  };
  /**
   * Returns a message describing the candidate's industry experience.
   * If the candidate has work history with an industry, returns a message listing the industries.
   * Otherwise, returns a message indicating that there are no industries associated with the candidate's work experiences.
   * @returns {string} The message describing the candidate's industry experience.
   */
  const industryMessage = () => {
    const industryType = handleIndustryType(candidateDetails?.workHistory);
    if (industryType?.length > 0) return `Your experiance is in following industry: ${industryType?.join(', ')}`;
    else return `There are no industries associated with your work experiences`;
  };

  /**
   * Calculates and returns the percentage value of a given numerator and denominator.
   * @param {number} num - The numerator of the fraction.
   * @param {number} den - The denominator of the fraction.
   * @returns {number} - The percentage value of the given fraction, rounded to the nearest whole number.
   */
  const percentage = (num, den) => {
    // console.log(num, den);
    const per = Math.round((num / den) * 100)
    return per;
  };

  /**
   * Renders a tooltip with the candidate's compensation information.
   * If the job is full-time, it displays a message comparing the
   * offered salary to the candidate's current compensation.
   * If the job is part-time, it displays a message comparing the
   * offered rate to the candidate's desired compensation.
   * The tooltip is triggered by clicking on the rendered text, and it
   * is only displayed if the user is on a mobile device and the
   * openTooltip state is set to 'salary'.
   * @param {string} salaryRating - The rating of the salary.
   * @param {object} jobPreference - The job preference object.
   * @param {number} salary - The offered salary.
   * @param {string} jobType - The type of the job (Full time or Part time).
   * @returns {JSX.Element} - The rendered JSX element with the tooltip.
   */
  const salaryMessage = (salaryRating, jobPreference, salary, jobType) => {
    const isFullTime = jobType === 'Full time';
    const candidateSalary = parseInt(
      isFullTime ? jobPreference?.total_ctc || 0 : jobPreference?.hourly_rate || 0
    );
    const offeredSalary = parseInt(salary || 0);

    /**
     * Renders a tooltip displaying the candidate's compensation information.
     * The tooltip is triggered by clicking on the rendered text.
     * It is conditionally displayed if the user is on a mobile device and
     * the openTooltip state is set to 'salary'.
     *
     * @param {string} text - The text to be displayed with the tooltip.
     * @returns {JSX.Element} - A JSX element containing the rendered tooltip.
     */

    const renderTooltip = (text) => (
      <Tooltip
        title={`Your compensation is ₹${formatNumber(candidateSalary)}`}
        arrow
        placement="top"
        open={(isMobile && openTooltip === 'salary') ? openTooltip : undefined}
      >
        <span style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => handleToggleTooltip('salary')}>
          {text}
        </span>
      </Tooltip>
    );
    /**
     * Renders a tooltip displaying the candidate's compensation information.
     * The tooltip is triggered by clicking on the rendered text.
     * It is conditionally displayed if the user is on a mobile device and
     * the openTooltip state is set to 'salary'.
     *
     * @param {string} text - The text to be displayed with the tooltip.
     * @returns {JSX.Element} - A JSX element containing the rendered tooltip.
     */
    const partTimeTooltip = (text) => (
      <Tooltip
        title={`Your compensation is ₹${candidateSalary} per hour`}
        arrow
        placement="top"
        open={(isMobile && openTooltip === 'salary') ? openTooltip : undefined}
      >
        <span style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => handleToggleTooltip('salary')}>
          {text}
        </span>
      </Tooltip>
    );

    if (isFullTime) {
      if (salaryRating === 2) {
        const message = offeredSalary > candidateSalary
          ? 'May be out of range versus '
          : 'No significant increment versus ';
        return (
          <>
            {message}
            {renderTooltip('current compensation')}. Please evaluate.
          </>
        );
      }

      const message = offeredSalary > candidateSalary
        ? 'Much higher than '
        : 'Lower than ';
      return (
        <>
          {message}
          {renderTooltip('current compensation')}.
        </>
      );
    }

    // Part-time logic
    if (salaryRating === 2) {
      const message = offeredSalary > candidateSalary
        ? 'Offered rate quite above '
        : 'Offered rate quite below ';
      return (
        (data?.compensation_model === 'per hour' ? <>
          {message}
          {partTimeTooltip('desired compensation')}. Please evaluate.
        </> : <>
          This project offers a {partTimeTooltip('fixed fee')}. Please evaluate.</>)
      );
    }

    return (
      data?.compensation_model === 'per hour' ? <>
        Offered rate well below {partTimeTooltip('desired compensation')}. Please evaluate.
      </> : <>
        This project offers a {partTimeTooltip('fixed fee')}. Please evaluate.</>
    );
  };

  /**
   * Handles toggling of the tooltip for mobile devices.
   * If on a mobile device, it sets the openTooltip state to the given title and
   * then sets it back to an empty string after 5 seconds.
   * @param {string} title - The title of the tooltip to toggle.
   */
  const handleToggleTooltip = (title) => {
    if (isMobile) {
      setOpenTooltip(title);
      setTimeout(() => setOpenTooltip(''), 5000);
    }
  };

  const expectedCtc = parseInt(candidateDetails?.user?.candidate?.job_preferences?.expected_ctc, 10);
  const jobCompensation = parseInt(data?.salary, 10);
  const comparisonText = expectedCtc > jobCompensation ? 'Less' : 'Greater';
  const truncatedDescription = showMore ? data?.job_overview : data?.job_overview?.substring(0, 50) + (data?.job_overview?.length > 50 ? '...' : '');

  return (
    <Box className="job-full-details">
      {/* header block */}
      <Box className="job-full-details-header">
        <Box className="header-top">
          <Stack direction="row" justifyContent={'space-between'} spacing={2}>
            <Box>
              <Typography className="title">{data?.title || 'NA'}</Typography>
              <Stack direction="row" alignItems={'center'} spacing={1}>
                {data?.is_company_name_confidential ? <Typography className="company-name">Company Name - Confidential</Typography> : <Typography className="company-name">{data?.company || ''}</Typography>}
                <Divider orientation="vertical" variant="middle" flexItem sx={{ backgroundColor: '#a5a297' }} />
                <Typography className="city-name">{data?.location || 'N/A'}</Typography>
                <Divider orientation="vertical" variant="middle" flexItem sx={{ backgroundColor: '#a5a297' }} />
                <Typography className="city-name">{data?.job_type || 'N/A'}</Typography>
              </Stack>
            </Box>

            {loading ? (
              <Stack direction="column" spacing={3}>
                <Skeleton height={40} width="100%" />
              </Stack>
            ) : (
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <Stack direction="row" alignItems={'center'} spacing={3}>
                  {<>
                    {data?.application_saved ? (
                      <Button sx={{ width: '150px' }}
                        variant="outlined"
                        size="large"
                        className="saved-btn"
                        onClick={handleOpenAutoUnsaveDialog}
                        startIcon={
                          <SvgIcon>
                            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="6" viewBox="0 0 8 6" fill="none">
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M7.77869 1.3764L3.45821 5.69687C3.16468 5.99041 2.67851 5.99041 2.3758 5.69687L0.220152 3.54122C-0.0733838 3.24769 -0.0733838 2.76152 0.220152 2.45881C0.513687 2.16527 0.999855 2.16527 1.30256 2.45881L2.92618 4.08243L6.70545 0.303159C6.99898 0.00962395 7.48515 0.00962395 7.78786 0.303159C8.07222 0.596695 8.07222 1.08286 7.77869 1.3764Z"
                                fill="white"
                              />
                            </svg>
                          </SvgIcon>
                        }
                      >
                        Job Saved
                      </Button>
                    ) : (
                      <Button sx={{ width: '150px' }}
                        variant="outlined"
                        size="large"
                        className="save-btn"
                        startIcon={<AddCircleIcon fontSize="large" />}
                        onClick={handleSubmit(handleSaveForLater)}
                        disabled={!dataSubmitted}
                      >
                        {dataSubmitted ? 'Save Job' : 'Saving...'}
                      </Button>
                    )}
                    <Button sx={{ width: '150px' }}
                      variant="contained"
                      size="large"
                      className={data?.external_link ? "external-apply-now-btn" : "apply-now-btn"}
                      endIcon={data?.external_link ? < SvgIcon >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 16 16" fill="none">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M15.5413 0.0581763C15.7159 0.130897 15.8613 0.276338 15.934 0.450867H15.9413C15.9776 0.538131 15.9995 0.632668 15.9995 0.727205V5.03953C15.9995 5.43949 15.6722 5.76673 15.2723 5.76673C14.8723 5.76673 14.5451 5.43949 14.5451 5.03953V2.47977L8.51653 8.50829C8.37109 8.64646 8.18929 8.71918 8.00021 8.71918C7.81114 8.71918 7.62934 8.65373 7.4839 8.50829C7.20029 8.22468 7.20029 7.76654 7.4839 7.48293L13.5124 1.45441H10.9527C10.5527 1.45441 10.2255 1.12717 10.2255 0.727205C10.2255 0.327242 10.5527 0 10.9527 0H15.265C15.3595 0 15.4541 0.0218161 15.5413 0.0581763ZM14.5375 8.0013C14.5375 7.60134 14.8648 7.27409 15.2648 7.27409C15.6647 7.27409 15.992 7.60134 15.992 8.0013V13.0554C15.992 14.677 14.6684 16.0005 13.0468 16.0005H2.94591C1.32424 16.0005 0.000732422 14.677 0.000732422 13.0554V2.9545C0.000732422 1.33283 1.32424 0.00932173 2.94591 0.00932173H7.99998C8.39994 0.00932173 8.72719 0.336564 8.72719 0.736526C8.72719 1.13649 8.39994 1.46373 7.99998 1.46373H2.94591C2.12417 1.46373 1.45514 2.13276 1.45514 2.9545V13.0554C1.45514 13.8771 2.12417 14.5461 2.94591 14.5461H13.0468C13.8685 14.5461 14.5375 13.8771 14.5375 13.0554V8.0013Z" fill="white" />
                        </svg>
                      </SvgIcon> : null}
                      onClick={handleApplyJob}
                    >
                      Apply Now
                    </Button>
                  </>
                  }
                  <ListItemIcon onClick={() => { handleOpenShareJobDialog(); setResumeActionAnchorEl(null); }}>
                    <ShareIcon fontSize="extraLarge" sx={{ width: "24px !important", height: "24px !important", cursor: "pointer" }} />
                  </ListItemIcon>
                </Stack>
              </Box>
            )}
          </Stack>
        </Box>
        {loading ? (
          <Box className="header-bottom">
            <Skeleton height={40} width="40%" />
          </Box>
        ) : (
          <Box className="header-bottom">
            <Stack direction="row" alignItems={'center'} justifyContent={'space-between'} sx={{ width: '100%' }} spacing={2}>
              <ToggleButtonGroup
                className="job-desc-toggle-btn"
                color="primary"
                value={alignment}
                exclusive
                onChange={handleToggleChange}
                aria-label="Platform"
              >
                <ToggleButton value="job-overview">Job Overview</ToggleButton>
                <ToggleButton value="job-desc">Job Details</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Box>
        )}
      </Box>

      {/* job overview block */}
      {loading ? (
        <Box className="job-full-overview">
          <Box className="job-overview-wrapper" mb={4}>
            <Card variant="outlined" className="requirements-match-card" sx={{ marginBottom: 3 }}>
              <CardContent>
                <Skeleton height={60} />
                <Skeleton height={40} width="60%" />
              </CardContent>
            </Card>
          </Box>
        </Box>
      ) : (
        <>
          <Box ref={jobFullOverviewRef} className="job-full-overview">
            <Box ref={jobOverviewRef} id="job-overview">
              <Box className="job-overview-wrapper" mb={4}>
                <Typography className="title">Job Overview</Typography>
                <Typography className="about-post-info" style={{ textAlign: 'justify', marginBottom: 15 }}>
                  {data?.company_overview}
                </Typography>

                {isMobile ?
                  <Typography className="about-post-info" style={{ textAlign: 'justify' }}>
                    {truncatedDescription}
                    {data?.job_overview.length > 50 && (
                      <span component="button" onClick={() => setShowMore(!showMore)}
                        style={{
                          textDecoration: 'underline',
                          color: 'var(--CTA-Active, #1575c6)',
                          cursor: 'pointer',
                        }}
                      >
                        {showMore ? 'Read less' : 'Read more'}
                      </span>
                    )}
                  </Typography>
                  : <Typography className="about-post-info" style={{ textAlign: 'justify' }}>{data?.job_overview}
                  </Typography>}
              </Box>

              <Card variant="outlined" className="requirements-match-card" sx={{ marginBottom: 3 }}>
                <CardContent>
                  {recommendedChip(data?.tj_score)}
                  <Typography className="title">Requirements Match</Typography>
                  <Typography className="info-text">
                    Matched against your educational background & work experience
                  </Typography>
                  <Box className="requirements-match" mt={1.5} >
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <Tooltip title={`You have ${candidateDetails?.qualification?.join(', ')} education`} arrow placement='top'>
                          <Box>
                            <Stack direction="row" align spacing={0.5}>
                              {requirementsMatch(data?.qualifications, candidateDetails?.rating?.qualification, 'Qualification')}
                              <Box>
                                <Typography className="req-title" sx={{ marginTop: '-2px' }}>Required Education</Typography>
                                <Typography className="req-text">
                                  {data?.qualifications?.join(' | ')}
                                </Typography>
                              </Box>
                            </Stack>
                          </Box>
                        </Tooltip>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Tooltip title={`You have ${candidateDetails?.totalExperience} years of experience`} arrow placement='top'>
                          <Box>
                            <Stack direction="row" spacing={0.5}>
                              {requirementsMatch(data?.experience, candidateDetails?.rating?.experienceMatchRating, 'Experience')}
                              <Box>
                                <Typography className="req-title" sx={{ marginTop: '-2px' }}>Required Work Experience</Typography>
                                <Typography className="req-text">
                                  {data?.experience ? `${data?.experience} Years` : 'N/A'}</Typography>
                              </Box>
                            </Stack>
                          </Box>
                        </Tooltip>

                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Tooltip title={<>
                          {data?.core_skills?.length > 0 ? (<div>Your preferred skills match = {percentage(coreMatchSkills?.length, data?.core_skills?.length)}%</div>) : ''}
                          <div>Your required skills match = {percentage(matchingSkills?.length, data?.key_skills?.length)}%</div>
                        </>} arrow placement="top">
                          <Box>
                            <Stack direction="row" spacing={0.5}>
                              {requirementsMatch(data?.key_skills?.length, candidateDetails?.rating?.skillMatchRating, 'Skills')}
                              <Box>
                                <Typography className="req-title" sx={{ marginTop: '-2px' }}>Required Skills</Typography>
                                <Typography className="req-text">
                                  {data?.core_skills?.length > 0 && (<>{coreMatchSkills?.length || 0}/{data.core_skills.length} Preferred<br /></>)}
                                  {matchingSkills ? matchingSkills?.length : 0}/{data?.key_skills ? data?.key_skills?.length : 0} Total
                                </Typography>
                              </Box>
                            </Stack>
                          </Box>
                        </Tooltip>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Tooltip title={industryMessage()} arrow placement='top'>
                          <Box>
                            <Stack direction="row" spacing={0.5}>
                              {requirementsMatch(data?.industry_sector, candidateDetails?.rating?.industry, 'Industry')}
                              <Box>
                                <Typography className="req-title" sx={{ marginTop: '-2px' }}>Industry Type</Typography>
                                <Typography className="req-text">{data?.industry_sector || 'N/A'}</Typography>
                              </Box>
                            </Stack>
                          </Box>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </Box>

                  <Divider sx={{ borderStyle: 'dashed', borderWidth: '1px', marginTop: '1rem', marginBottom: '1rem' }} />

                  <Box className="preferences-match">
                    <Typography className="title">Preferences Match</Typography>
                    <Typography className="pref-info-text" component="p">
                      Matched against your defined job preferences.{' '}
                      <span style={{
                        textDecoration: 'underline',
                        color: 'var(--CTA-Active, #1575c6)',
                        cursor: 'pointer'
                      }}
                        onClick={handleOpenJobPreferenceDrawer}>
                        click here to update preferences
                      </span>
                    </Typography>
                    <Box mt={2}>
                      <Stack spacing={1.5}>
                        <Grid container>
                          <Grid item xs={12} md={3}>
                            <Stack direction="row" alignItems={'center'} spacing={0.5}>
                              {matchIcon(
                                data?.salary,
                                candidateDetails?.rating?.salaryMatchRating,
                                requirementMatchItem[0]
                              )}
                            </Stack>
                          </Grid>
                          <Grid item xs={12} md={9}>
                            <Box sx={{ marginLeft: { xs: '20px' } }}>
                              <Stack direction={{ xs: 'column', md: 'row' }} alignItems={'flex-start'} flexWrap="wrap" spacing={{ xs: 0, md: 0.7 }}>
                                {(data?.is_compensation_confidential || !data?.salary) ?
                                  <Typography className="pref-match-info">Not Disclosed</Typography> : (<>
                                    <Typography className="pref-match-info">
                                      ₹{data?.job_type === 'Full time' ? formatNumber(data?.salary) : Intl.NumberFormat('en-IN').format(data?.salary)}{' '}
                                      {data?.job_type === 'Full time'
                                        ? data?.compensation ? `(${data.compensation})` : ''
                                        : data?.compensation_model === 'Fixed Fee' ? `(${data.compensation_model})` : data.compensation_model}
                                    </Typography>
                                  </>)}
                                {data?.salary && candidateDetails?.rating?.salaryMatchRating < 3 &&
                                  <Typography className="pref-match-info notice-period-error">
                                    {salaryMessage(
                                      candidateDetails?.rating?.salaryMatchRating,
                                      candidateDetails?.user?.candidate?.job_preferences,
                                      data?.salary,
                                      data?.job_type
                                    )}
                                  </Typography>
                                }
                              </Stack>
                            </Box>
                          </Grid>
                        </Grid>
                        <Grid container>
                          <Grid item xs={12} md={3}>
                            <Stack direction="row" alignItems={'center'} spacing={0.5}>
                              {matchIcon(
                                data?.location,
                                candidateDetails?.rating?.location,
                                requirementMatchItem[1]
                              )}
                            </Stack>
                          </Grid>
                          <Grid item xs={12} md={9}> {/* ---------location---------------- */}
                            <Box sx={{ marginLeft: { xs: '20px' } }}>
                              <Stack direction={{ xs: 'column', md: 'row' }} alignItems={'flex-start'} flexWrap="wrap" spacing={{ xs: 0, md: 0.7 }}>
                                <Typography className="pref-match-info" sx={{ flexShrink: 0 }}>
                                  {(data?.job_type !== 'Full time' &&
                                    data?.work_mode === 'Work from Home' &&
                                    candidateDetails?.rating?.format === 2 &&
                                    candidateDetails?.rating?.location === 2)
                                    ? 'Remote'
                                    : (data?.location || 'NA')
                                  }
                                </Typography>
                                {candidateDetails?.rating?.location < 2 &&
                                  <Typography className="pref-match-info notice-period-error">
                                    (Does not match any of your{' '}
                                    <Tooltip
                                      title={`Your preferred locations are ${candidateDetails?.user?.candidate?.job_preferences?.prefered_location?.join(', ')}`}
                                      arrow placement="top"
                                      open={(isMobile && openTooltip === 'location') ? openTooltip : undefined}
                                    >
                                      <span style={{ textDecoration: 'underline' }}
                                        onClick={() => handleToggleTooltip('location')}>
                                        preferred locations
                                      </span>
                                    </Tooltip>
                                    )
                                  </Typography>
                                }
                              </Stack>
                            </Box>
                          </Grid>
                        </Grid>
                        <Grid container>
                          <Grid item xs={12} md={3}>
                            <Stack direction="row" alignItems={'center'} spacing={0.5}>
                              {matchIcon(
                                data?.work_mode,
                                candidateDetails?.rating?.format,
                                requirementMatchItem[2]
                              )}
                            </Stack>
                          </Grid>
                          <Grid item xs={12} md={9}>
                            <Box sx={{ marginLeft: { xs: '20px' } }}>
                              <Stack direction={{ xs: 'column', md: 'row' }} alignItems={'flex-start'} flexWrap="wrap" spacing={{ xs: 0, md: 0.7 }}>
                                <Typography className="pref-match-info" sx={{ flexShrink: 0 }}>{data?.work_mode || 'NA'}</Typography>
                                {candidateDetails?.rating?.format < 2 &&
                                  <Typography className="pref-match-info notice-period-error">
                                    (Does not match any of your{' '}
                                    <Tooltip
                                      title={`Your preferred work formats is ${candidateDetails?.user?.candidate?.job_preferences?.work_format?.join(', ')}`}
                                      arrow placement="top"
                                      open={(isMobile && openTooltip === 'work_format') ? openTooltip : undefined}
                                    >
                                      <span style={{ textDecoration: 'underline' }}
                                        onClick={() => handleToggleTooltip('work_format')}>
                                        preferred job formats
                                      </span>
                                    </Tooltip>
                                    )
                                  </Typography>
                                }
                              </Stack>
                            </Box>
                          </Grid>
                        </Grid>
                        <Grid container>
                          <Grid item xs={12} md={3}>
                            <Stack direction="row" alignItems={'center'} spacing={0.5}>
                              {matchIcon(
                                data?.job_type,
                                candidateDetails?.rating?.type,
                                requirementMatchItem[3]
                              )}
                            </Stack>
                          </Grid>
                          < Grid item xs={12} md={9}>
                            <Box sx={{ marginLeft: { xs: '20px' } }}>
                              <Stack direction={{ xs: 'column', md: 'row' }} alignItems={'flex-start'} flexWrap="wrap" spacing={{ xs: 0, md: 0.7 }}>
                                <Typography className="pref-match-info" sx={{ flexShrink: 0 }}>{data?.job_type || 'NA'}</Typography>
                                {candidateDetails?.rating?.type < 2 &&
                                  <Typography className="pref-match-info notice-period-error">
                                    (Does not match any of your{' '}
                                    <Tooltip
                                      title={`Your preferred job type is ${candidateDetails?.user?.candidate?.job_preferences?.job_type?.join(', ')}`}
                                      arrow placement="top"
                                      open={(isMobile && openTooltip === 'job_type') ? openTooltip : undefined}
                                    >
                                      <span style={{ textDecoration: 'underline' }}
                                        onClick={() => handleToggleTooltip('job_type')}>
                                        preferred job types
                                      </span>
                                    </Tooltip>
                                    )
                                  </Typography>
                                }
                              </Stack>
                            </Box>
                          </Grid>
                        </Grid>
                        <Grid container>
                          <Grid item xs={12} md={3}>
                            <Stack direction="row" alignItems={'center'} spacing={0.5}>
                              {matchIcon(
                                data?.job_shift,
                                candidateDetails?.rating?.shift,
                                requirementMatchItem[4]
                              )}
                            </Stack>
                          </Grid>
                          <Grid item xs={12} md={9}>
                            <Box sx={{ marginLeft: { xs: '20px' } }}>
                              <Stack direction={{ xs: 'column', md: 'row' }} alignItems={'flex-start'} flexWrap="wrap" spacing={{ xs: 0, md: 0.7 }}>
                                <Typography className="pref-match-info" sx={{ flexShrink: 0 }}>
                                  {data?.job_shift ? `${data?.job_shift}` : 'NA'}
                                </Typography>
                                {candidateDetails?.rating?.shift < 2 &&
                                  <Typography className="pref-match-info notice-period-error">
                                    (Does not match any of your{' '}
                                    <Tooltip
                                      title={`Your preferred job shift is ${candidateDetails?.user?.candidate?.job_preferences?.preferred_job_shifts?.join(', ')} as your preferred shift`}
                                      arrow placement="top"
                                      open={(isMobile && openTooltip === 'job_shift') ? openTooltip : undefined}
                                    >
                                      <span style={{ textDecoration: 'underline' }}
                                        onClick={() => handleToggleTooltip('job_shift')}>
                                        preferred shift
                                      </span>
                                    </Tooltip>
                                    )
                                  </Typography>
                                }
                              </Stack>
                            </Box>
                          </Grid>
                        </Grid>
                        {data?.job_type === 'Full time' ?
                          <Grid container>
                            <Grid item xs={12} md={3}>
                              <Stack direction="row" alignItems={'center'} spacing={0.5}>
                                {matchIcon(
                                  data?.notice_period,
                                  candidateDetails?.rating?.notice,
                                  requirementMatchItem[5]
                                )}
                              </Stack>
                            </Grid>
                            <Grid item xs={12} md={9}>
                              <Box sx={{ marginLeft: { xs: '20px' } }}>
                                <Stack direction={{ xs: 'column', md: 'row' }} alignItems={'flex-start'} flexWrap="wrap" spacing={{ xs: 0, md: 0.7 }}>
                                  <Typography className="pref-match-info" sx={{ flexShrink: 0 }}>{data?.notice_period ? ((data?.notice_period === '0 days (immediate)' || data?.notice_period === 'No preference') ? `${data?.notice_period}` : `Upto ${data?.notice_period} days`) : 'NA'}</Typography>
                                  {candidateDetails?.rating?.notice < 2 &&
                                    <Typography className="pref-match-info notice-period-error">
                                      (Lower than your{' '}
                                      <Tooltip
                                        title={`Your current notice period is ${candidateDetails?.user?.candidate?.job_preferences?.notice_period} days`}
                                        arrow placement="top"
                                        open={(isMobile && openTooltip === 'notice_period') ? openTooltip : undefined}
                                      >
                                        <span style={{ textDecoration: 'underline' }}
                                          onClick={() => handleToggleTooltip('notice_period')}>
                                          current notice period
                                        </span>
                                      </Tooltip>
                                      )
                                    </Typography>
                                  }
                                </Stack>
                              </Box>
                            </Grid>
                          </Grid> :
                          <Grid container>
                            <Grid item xs={12} md={3}>
                              <Stack direction="row" alignItems={'center'} spacing={0.5}>
                                {matchIcon(
                                  data?.weekly_time_commitment,
                                  candidateDetails?.rating?.availableHour,
                                  requirementMatchItem[6]
                                )}
                              </Stack>
                            </Grid>
                            <Grid item xs={12} md={9}>
                              <Box sx={{ marginLeft: { xs: '20px' } }}>
                                <Stack direction={{ xs: 'column', md: 'row' }} alignItems={'flex-start'} flexWrap="wrap" spacing={{ xs: 0, md: 0.7 }}>
                                  <Typography className="pref-match-info" sx={{ flexShrink: 0 }}>
                                    {data?.weekly_time_commitment}{' '} hours/ week, for {data?.total_time_commitment_requirement}{' '} weeks, starting on{' '} {dayjs(data?.start_date).format('MMM DD, YYYY')}
                                  </Typography>
                                  {candidateDetails?.rating?.availableHour < 2 &&
                                    <Typography className="pref-match-info notice-period-error">
                                      (Greater than your{' '}
                                      <Tooltip
                                        title={`Your current time commitment is ${candidateDetails?.user?.candidate?.job_preferences?.hour} hours per week`}
                                        arrow placement="top"
                                        open={(isMobile && openTooltip === 'hour') ? openTooltip : undefined}
                                      >
                                        <span style={{ textDecoration: 'underline' }}
                                          onClick={() => handleToggleTooltip('hour')}>
                                          time commitment
                                        </span>
                                      </Tooltip>
                                      )
                                    </Typography>
                                  }
                                </Stack>
                              </Box>
                            </Grid>
                          </Grid>}

                      </Stack>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Card variant="outlined" className="skills-match-card" sx={{ marginBottom: 3 }}>
                <CardContent>
                  <Box>
                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent={'space-between'} >
                      <Box>
                        <Typography className="title">Skills Match Analysis</Typography>
                      </Box>
                      <Box>
                      </Box>
                    </Stack>
                    <Typography className="info-text">
                      You possess {matchingSkills?.length || 0} out {data?.key_skills?.length || 0} required skills. {''}
                      <span style={{
                        textDecoration: 'underline',
                        color: 'var(--CTA-Active, #1575c6)',
                        cursor: 'pointer'
                      }}
                        onClick={handleOpenSkillsDrawer}>
                        click here to add missing skills
                      </span>
                    </Typography>
                  </Box>

                  <Stack direction="row" alignItems={'center'} spacing={0.1} mb={1} mt={1}>
                    <StarIcon className="star-icon" sx={{ width: '12px', color: '#faaf00', marginLeft: 'auto' }} />
                    <Typography sx={{
                      display: 'inline-flex',
                      color: '#374151',
                      fontFamily: '"Inter", sans-serif',
                      fontSize: '12px',
                      fontStyle: 'normal',
                      fontWeight: '500',
                      lineHeight: '18px',
                      margin: '0 5px!important'
                    }}>Denote preferred skills</Typography>
                    <Tooltip title={`For success in this job, these skills are considered to be the most important`} arrow placement="top" >
                      <SvgIcon sx={{ width: '12px' }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M0 6C0 9.30764 2.69236 12 6 12C9.30764 12 12 9.30764 12 6C12 2.69236 9.30764 0 6 0C2.69236 0 0 2.69236 0 6ZM6 1.09091C8.70764 1.09091 10.9091 3.29236 10.9091 6C10.9091 8.70764 8.70764 10.9091 6 10.9091C3.29236 10.9091 1.09091 8.70764 1.09091 6C1.09091 3.29236 3.29236 1.09091 6 1.09091Z" fill="#1575C6" />
                          <path d="M6.00004 8.74382C6.30114 8.74382 6.5455 8.49946 6.5455 8.19837V5.41655C6.5455 5.11546 6.30114 4.87109 6.00004 4.87109C5.69895 4.87109 5.45459 5.11546 5.45459 5.41655V8.19837C5.45459 8.49946 5.69895 8.74382 6.00004 8.74382Z" fill="#1575C6" />
                          <path d="M6.00004 4.34599C6.03495 4.34599 6.07204 4.34163 6.10695 4.33508C6.14186 4.32853 6.17677 4.31763 6.2095 4.30453C6.24223 4.29144 6.27277 4.27399 6.30332 4.25435C6.33168 4.23472 6.36004 4.21072 6.38623 4.18672C6.48659 4.08417 6.5455 3.94453 6.5455 3.80053C6.5455 3.65653 6.48659 3.5169 6.38623 3.41435C6.25968 3.28781 6.06986 3.23108 5.89313 3.26599C5.85823 3.27253 5.82332 3.28344 5.79059 3.29653C5.75786 3.30963 5.72732 3.32708 5.69677 3.34672C5.66841 3.36635 5.64004 3.39035 5.61386 3.41435C5.5135 3.5169 5.45459 3.65653 5.45459 3.80053C5.45459 3.94453 5.5135 4.08417 5.61386 4.18672C5.71641 4.28708 5.85604 4.34599 6.00004 4.34599Z" fill="#1575C6" />
                        </svg>
                      </SvgIcon>
                    </Tooltip>
                  </Stack>

                  <Stack direction="row" flexWrap="wrap" sx={{ marginBottom: '1rem' }} >
                    {data?.key_skills
                      ? data?.key_skills.map((skill, index) => (
                        <Chip
                          key={index}
                          icon={
                            matchingSkills?.includes(skill) ? (
                              <Box className="match-icon">
                                <SvgIcon>
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="8"
                                    height="6"
                                    viewBox="0 0 8 6"
                                    fill="none"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M7.77869 1.3764L3.45821 5.69687C3.16468 5.99041 2.67851 5.99041 2.3758 5.69687L0.220152 3.54122C-0.0733838 3.24769 -0.0733838 2.76152 0.220152 2.45881C0.513687 2.16527 0.999855 2.16527 1.30256 2.45881L2.92618 4.08243L6.70545 0.303159C6.99898 0.00962395 7.48515 0.00962395 7.78786 0.303159C8.07222 0.596695 8.07222 1.08286 7.77869 1.3764Z"
                                      fill="white"
                                    />
                                  </svg>
                                </SvgIcon>
                              </Box>
                            ) : (
                              <Box className="error-match-icon">
                                <SvgIcon>
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="6"
                                    height="6"
                                    viewBox="0 0 6 6"
                                    fill="none"
                                  >
                                    <path
                                      d="M0.134092 5.23008L2.36417 3L0.134092 0.769922C0.0918098 0.728272 0.0581919 0.678662 0.0351778 0.623955C0.0121637 0.569247 0.000208773 0.510524 2.71012e-06 0.451173C-0.000203353 0.391823 0.0113435 0.333018 0.0339772 0.278152C0.0566109 0.223286 0.0898834 0.173444 0.131876 0.131501C0.173868 0.0895583 0.223749 0.0563443 0.278642 0.0337753C0.333535 0.0112063 0.392353 -0.000271149 0.451703 4.86231e-06C0.511054 0.000280873 0.569763 0.0123049 0.624443 0.0353835C0.679123 0.0584621 0.728693 0.0921385 0.770294 0.13447L2.99962 2.36455L5.2297 0.13447C5.2713 0.0921385 5.32087 0.0584621 5.37555 0.0353835C5.43023 0.0123049 5.48894 0.000280873 5.54829 4.86231e-06C5.60764 -0.000271149 5.66646 0.0112063 5.72135 0.0337753C5.77624 0.0563443 5.82613 0.0895583 5.86812 0.131501C5.91011 0.173444 5.94338 0.223286 5.96602 0.278152C5.98865 0.333018 6.0002 0.391823 5.99999 0.451173C5.99978 0.510524 5.98783 0.569247 5.96482 0.623955C5.9418 0.678662 5.90818 0.728272 5.8659 0.769922L3.63507 3L5.86515 5.23008C5.90743 5.27173 5.94105 5.32134 5.96407 5.37605C5.98708 5.43075 5.99904 5.48948 5.99924 5.54883C5.99945 5.60818 5.9879 5.66698 5.96527 5.72185C5.94263 5.77671 5.90936 5.82656 5.86737 5.8685C5.82538 5.91044 5.7755 5.94366 5.7206 5.96622C5.66571 5.98879 5.60689 6.00027 5.54754 6C5.48819 5.99972 5.42948 5.9877 5.3748 5.96462C5.32012 5.94154 5.27055 5.90786 5.22895 5.86553L2.99962 3.63545L0.769545 5.86553C0.685044 5.94877 0.571075 5.99524 0.452464 5.99483C0.333852 5.99442 0.220209 5.94715 0.136288 5.86333C0.0523672 5.77951 0.00496864 5.66592 0.00441704 5.54731C0.00386544 5.4287 0.0502055 5.31468 0.133343 5.23008H0.134092Z"
                                      fill="white"
                                    />
                                  </svg>
                                </SvgIcon>
                              </Box>
                            )
                          }
                          label={
                            data?.core_skills?.includes(skill) ?
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <StarIcon className="star-icon" sx={{ color: '#faaf00', padding: '5px', marginLeft: 'auto' }} />
                                <span>{skill}</span>
                              </Box> : skill
                          }
                          variant="outlined"
                          className={matchingSkills?.includes(skill) ? 'match-skill' : 'destructive-skill'}
                        />
                      ))
                      : 'No Skills'}
                  </Stack>
                </CardContent>
              </Card>
            </Box>

            <Divider sx={{ borderStyle: 'dashed', borderWidth: '1px', marginTop: '1rem', marginBottom: '1rem' }} />

            <Box ref={jobDescRef} id="job-desc" className="job-description">
              <Typography className="title">Job Details</Typography>
              <Typography className="info-text list-style" >
                <div dangerouslySetInnerHTML={{ __html: data?.description }} />
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: { xs: 'block', md: 'none' } }} padding={1}>
            <Stack direction="row" alignItems={'center'} justifyContent={"center"} spacing={2}>
              {<>
                {data?.application_saved ? (
                  <Button fullWidth
                    variant="outlined"
                    size="large"
                    className="saved-btn"
                    onClick={handleOpenAutoUnsaveDialog}
                    startIcon={
                      <SvgIcon sx={{ marginRight: '0px !important' }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="6" viewBox="0 0 8 6" fill="none">
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M7.77869 1.3764L3.45821 5.69687C3.16468 5.99041 2.67851 5.99041 2.3758 5.69687L0.220152 3.54122C-0.0733838 3.24769 -0.0733838 2.76152 0.220152 2.45881C0.513687 2.16527 0.999855 2.16527 1.30256 2.45881L2.92618 4.08243L6.70545 0.303159C6.99898 0.00962395 7.48515 0.00962395 7.78786 0.303159C8.07222 0.596695 8.07222 1.08286 7.77869 1.3764Z"
                            fill="white"
                          />
                        </svg>
                      </SvgIcon>
                    }
                  >
                    Job Saved
                  </Button>
                ) : (
                  <Button fullWidth
                    variant="outlined"
                    size="large"
                    className="save-btn"
                    startIcon={<AddCircleIcon fontSize="large" />}
                    onClick={handleSubmit(handleSaveForLater)}
                    disabled={!dataSubmitted}
                  >
                    {dataSubmitted ? 'Save Job' : 'Saving...'}
                  </Button>
                )}
                <Button fullWidth
                  variant="contained"
                  size="large"
                  className={data?.external_link ? "external-apply-now-btn" : "apply-now-btn"}
                  endIcon={data?.external_link ? < SvgIcon >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 16 16" fill="none">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M15.5413 0.0581763C15.7159 0.130897 15.8613 0.276338 15.934 0.450867H15.9413C15.9776 0.538131 15.9995 0.632668 15.9995 0.727205V5.03953C15.9995 5.43949 15.6722 5.76673 15.2723 5.76673C14.8723 5.76673 14.5451 5.43949 14.5451 5.03953V2.47977L8.51653 8.50829C8.37109 8.64646 8.18929 8.71918 8.00021 8.71918C7.81114 8.71918 7.62934 8.65373 7.4839 8.50829C7.20029 8.22468 7.20029 7.76654 7.4839 7.48293L13.5124 1.45441H10.9527C10.5527 1.45441 10.2255 1.12717 10.2255 0.727205C10.2255 0.327242 10.5527 0 10.9527 0H15.265C15.3595 0 15.4541 0.0218161 15.5413 0.0581763ZM14.5375 8.0013C14.5375 7.60134 14.8648 7.27409 15.2648 7.27409C15.6647 7.27409 15.992 7.60134 15.992 8.0013V13.0554C15.992 14.677 14.6684 16.0005 13.0468 16.0005H2.94591C1.32424 16.0005 0.000732422 14.677 0.000732422 13.0554V2.9545C0.000732422 1.33283 1.32424 0.00932173 2.94591 0.00932173H7.99998C8.39994 0.00932173 8.72719 0.336564 8.72719 0.736526C8.72719 1.13649 8.39994 1.46373 7.99998 1.46373H2.94591C2.12417 1.46373 1.45514 2.13276 1.45514 2.9545V13.0554C1.45514 13.8771 2.12417 14.5461 2.94591 14.5461H13.0468C13.8685 14.5461 14.5375 13.8771 14.5375 13.0554V8.0013Z" fill="white" />
                    </svg>
                  </SvgIcon> : null}
                  onClick={handleApplyJob}
                >
                  Apply Now
                </Button>
              </>
              }
            </Stack>
          </Box>
        </>
      )}

      {/* apply now chatboat drawer */}
      <Drawer
        open={openChatBotDrawer}
        className="chat-bot-drawer-dialog"
        anchor={'right'}
        onClose={toggleChatBotDrawer(false)}
      >
        <ChatBotDrawer
          questions={data?.question}
          onClose={toggleChatBotDrawer(false)}
          onSelected={handleSelectedResume}
          tab={'2'}
          data={data}
          candidateDetails={candidateDetails}
          matchingSkills={matchingSkills}
          handleCloseDrawer={handleCloseDrawer}
          isPartTimeJob={data?.job_type?.toLowerCase() == 'full time' ? false : true}
          onUpdatePreference={handleUpdatePreference}
        />
      </Drawer>

      {/* update job preferences drawer */}
      <Drawer open={openJobPreference} anchor={'right'} onClose={handleCloseJobPreferenceDrawer}>
        <JopApplicationDrawer
          onClose={handleCloseJobPreferenceDrawer}
          data={candidateDetails?.user?.candidate?.job_preferences}
          onUpdatePreference={handleUpdatePreference}
        />
      </Drawer>

      {/* update relevent skills drawer */}
      <Drawer open={openSkillsDrawer} anchor={'right'} onClose={handleCloseSkillsDrawer}>
        <UpdateReleventSkillsDrawer
          onClose={handleCloseSkillsDrawer}
          workExperience={candidateDetails?.workHistory}
          education={candidateDetails?.educations}
          certificate={candidateDetails?.certificate}
          skillsUpdate={handleSkillsUpdate}
          nonMatchingSkills={nonMatchingSkills}
          matchingSkills={matchingSkills}
          setNonMatchingSkills={handleNonMatchingSkills}
        />
      </Drawer>

      {/* auto save job app dialog */}
      <Dialog
        open={autoSaveDialogOpen}
        onClose={handleCloseAutoSaveDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className="auto-save-app-dialog"
      >
        <AutoSavingJobAppDialog />
      </Dialog>
      {/* unsaved job */}
      {isMobile ?
        <Drawer
          anchor='bottom'
          open={autoUnsaveDialogOpen}
          onClose={handleCloseAutoUnsaveDialog}
          className="mobile-bottomsheet-dialog action-dialog-card external-job-app-dialog"
        >
          <UnsaveCard onClose={handleCloseAutoUnsaveDialog} onUnsave={handleUnsaveJob} title={data?.title} />
        </Drawer>
        : <Dialog
          open={autoUnsaveDialogOpen}
          onClose={handleCloseAutoUnsaveDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          className="action-dialog-card external-list-dialog"
        > <DialogContent>
            <UnsaveCard onClose={handleCloseAutoUnsaveDialog} onUnsave={handleUnsaveJob} title={data?.title} />
          </DialogContent>
        </Dialog>}
      {/* external job and download resume dialog */}
      {isMobile ?
        <Drawer
          anchor="bottom"
          open={openExternalJob}
          onClose={handleCloseExternalJobDialog}
          className="mobile-bottomsheet-dialog action-dialog-card external-job-app-dialog"
        >
          <ExternalJob onClose={handleCloseExternalJobDialog} externalJobStatus={handleExternalJobStatus} downloadResume={handleDownloadResume} candidateDetails={candidateDetails} matchingSkills={matchingSkills} job={data} tab={'2'} />

        </Drawer>
        : <Dialog
          open={openExternalJob}
          onClose={handleCloseExternalJobDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          className="action-dialog-card external-list-dialog"
        > <DialogContent>
            <ExternalJob onClose={handleCloseExternalJobDialog} externalJobStatus={handleExternalJobStatus} downloadResume={handleDownloadResume} candidateDetails={candidateDetails} matchingSkills={matchingSkills} job={data} tab={'2'} />
          </DialogContent>
        </Dialog>}

      {/* external job status */}
      {isMobile ?
        <Drawer
          anchor="bottom"
          open={openExternalJobStatus}
          onClose={handleCloseExternalJobStatusDialog}
          className="mobile-bottomsheet-dialog action-dialog-card external-job-app-dialog"
        >
          <ExternalJobStatus onClose={handleCloseExternalJobStatusDialog} job={data} tab={'2'} candidateDetails={candidateDetails} matchingSkills={matchingSkills} />
        </Drawer> :
        <Dialog
          open={openExternalJobStatus}
          onClose={handleCloseExternalJobStatusDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          className="action-dialog-card external-list-dialog"
        > <DialogContent>
            <ExternalJobStatus onClose={handleCloseExternalJobStatusDialog} job={data} tab={'2'} candidateDetails={candidateDetails} matchingSkills={matchingSkills} />
          </DialogContent>
        </Dialog>}
      {/* Share job mobile view */}
      {isMobile ?
        <Drawer
          anchor="bottom"
          open={shareJobDialogOpen}
          onClose={handleCloseShareJobDialog}
          className="forgot-password-dialog"
        >
          <ShareJob jobId={data?.jobid} onClose={handleCloseShareJobDialog} />
        </Drawer> :
        /* Share job dialog */
        <Dialog
          open={shareJobDialogOpen}
          onClose={handleCloseShareJobDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          className="external-job-app-dialog"
        >
          <ShareJob jobId={data?.jobid} onClose={handleCloseShareJobDialog} />
        </Dialog>
      }
    </Box>
  );
};

export default JobFullDetails;
